import React from "react";
import { Box, DialogTitle, Typography } from "@mui/material";
import { alpha, lighten } from "@mui/material/styles";
import { settings } from "app";

// The new modal look (gradient header, rounded paper, tall buttons) is per tenant - settings.style.modernUi.
// Without it every helper below falls back to the old plain modal.
const modernUi = settings.style.modernUi;

// `color` is a palette key ("primary", "error", ...) so error modals keep the same look in red.
const modalGradient = (theme, color = "primary") => {
  const { main, dark } = theme.palette[color];
  return `linear-gradient(135deg, ${dark} 0%, ${main} 55%, ${lighten(main, 0.3)} 100%)`;
};

// Shared shape for every modal action button (height, radius, padding).
export const modalButtonSx = modernUi
  ? {
      minHeight: 44,
      borderRadius: 3,
      px: 3,
      fontWeight: 600,
    }
  : {};

// Cancel button shape; colors come from the Button's own variant="outlined" color="error".
export const modalCancelButtonSx = modalButtonSx;

// Solid primary confirm button with a soft primary-tinted shadow.
export const modalConfirmButtonSx = modernUi
  ? {
      ...modalButtonSx,
      color: "#fff",
      bgcolor: "primary.main",
      boxShadow: theme => `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
      "&:hover": {
        bgcolor: "primary.dark",
        boxShadow: theme => `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
      },
      "&.Mui-disabled": {
        bgcolor: "action.disabledBackground",
        color: "action.disabled",
        boxShadow: "none",
      },
    }
  : {};

// Button props (variant, color, ...) that only the new look sets: `{...modernUiProps({ color: "error" })}` keeps a
// button's old defaults without settings.style.modernUi.
export const modernUiProps = props => (modernUi ? props : {});

// DialogContent padding of the shared modals.
export const modalContentSx = modernUi ? { p: 4 } : { px: 4 };

// The old header: a plain bar in the palette color with a bold white title. `legacySx` restores a dialog whose
// old title looked different.
const LegacyModalTitle = ({ title, subtitle, color, legacySx }) => (
  <DialogTitle sx={[{ bgcolor: `${color}.main`, color: "#fff", fontWeight: "bold" }, legacySx]}>
    {title}
    {subtitle ? (
      <Typography
        component="span"
        variant="body2"
        sx={{ display: "block" }}
      >
        {subtitle}
      </Typography>
    ) : null}
  </DialogTitle>
);

// Shared modal header (ConfirmModal, FormModal): gradient title bar with an optional icon badge,
// title and subtitle. Use it as the DialogTitle of any custom Dialog so it matches them.
const ModalTitle = ({ icon, title, subtitle, color = "primary", legacySx }) =>
  !modernUi ? (
    <LegacyModalTitle
      title={title}
      subtitle={subtitle}
      color={color}
      legacySx={legacySx}
    />
  ) : (
    // component="div" - the header holds block elements, which are invalid inside the default <h2>.
    <DialogTitle
      component="div"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        py: 2.5,
        px: 3,
        color: "#fff",
        background: theme => modalGradient(theme, color),
      }}
    >
      {icon ? (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 44,
            height: 44,
            borderRadius: 2,
            color: "#fff",
            bgcolor: alpha("#fff", 0.18),
            border: `1px solid ${alpha("#fff", 0.3)}`,
            "& svg": { fontSize: 24 },
          }}
        >
          {icon}
        </Box>
      ) : null}
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ typography: "h6", fontWeight: 600, lineHeight: 1.3 }}>{title}</Box>
        {subtitle ? (
          <Typography
            variant="body2"
            sx={{ color: alpha("#fff", 0.85) }}
          >
            {subtitle}
          </Typography>
        ) : null}
      </Box>
    </DialogTitle>
  );

// The header badge reuses the trigger's icon unless an explicit one is given:
// MUI Button keeps it in `startIcon`, react-admin Button in `children`.
export const getTriggerIcon = triggerButton => {
  const { startIcon, children } = triggerButton?.props ?? {};
  if (startIcon) return startIcon;
  return React.isValidElement(children) ? children : null;
};

// Dialog sx with the shared rounded paper. An sx array is deep-merged, so callers that
// override "& .MuiDialog-paper" (sizes etc.) keep the rounded corners.
export const withModalSx = (...sx) => [
  modernUi && { "& .MuiDialog-paper": { borderRadius: 2 } },
  ...sx.flatMap(item => (Array.isArray(item) ? item : [item])),
];

export default ModalTitle;
